import React, { memo, useContext } from 'react';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import T from '@prisjakt/ui-translate';
import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import PropTypes from 'prop-types';
import PriceLabel from '@prisjakt/ui-price-label';
import Currency, { formatCurrency } from '@prisjakt/ui-currency';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import Graph from '../../../../components/graph';
import PriceHistoryCurve from '../../../../components/graph/components/price-history-curve';
import PricePrediction from '../../../../components/graph/components/price-prediction';

import './index.scss';

const styles = bemGenerator('price-history', ['summary', 'information']);

// TODO: This should be market specific
// maybe a <Date /> component similar to <Currency /> ?
const formatDate = d =>
  `${d.getFullYear()}-${`0${d.getMonth() + 1}`.slice(
    -2
  )}-${`0${d.getDate()}`.slice(-2)}`;

const PriceHistoryLoader = props => {
  const { t, market } = useContext(AppContext);

  if (!props.pageInfo) {
    return null;
  }

  const { lowestPrice } = props.pageInfo;

  if (!props.nodes.length) {
    return null;
  }

  let defaultGraphs;
  let defaultXScaleData;
  let defaultYScaleData;

  if (!props.graphs) {
    defaultGraphs = [
      {
        name: 'price-history',
        legend: {
          label: t('product', 'price-history.actual-price'),
          color: '#4895B6',
          lineType: 'normal',
        },
        render: graphProps => (
          <PriceHistoryCurve
            key={graphProps.name}
            data={props.nodes}
            {...graphProps}
          />
        ),
      },
    ];

    defaultXScaleData = props.nodes.map(({ date }) => new Date(date));
    defaultYScaleData = props.nodes.map(x => x.lowestPrice);

    if (props.predictedPrices && props.predictedPrices.length > 0) {
      defaultGraphs.push({
        name: 'price-prediction',
        legend: {
          label: t('product', 'price-history.pre-release'),
          color: '#5fbc40',
          lineType: 'dashed',
          description: t('product', 'price-history.pre-release-info'),
        },
        render: graphProps => (
          <PricePrediction
            key={graphProps.name}
            data={props.predictedPrices}
            {...graphProps}
          />
        ),
      });

      defaultXScaleData = defaultXScaleData.concat(
        props.predictedPrices.map(({ date }) => new Date(date))
      );
      defaultYScaleData = defaultYScaleData.concat(
        props.predictedPrices.map(({ price }) => price)
      );
    }
  }

  return (
    <div className={cn(styles.block, props.className)}>
      <Panel
        header={
          props.withTitle &&
          props.route && (
            <PanelHeader
              title={t('product', 'price-history.title')}
              margin="bottom"
            >
              <Link to={props.route}>{t('common', 'button.show-more')}</Link>
            </PanelHeader>
          )
        }
        footer={
          market.showProductInformationTexts &&
          props.productName && (
            <div className={styles.information}>
              <T domain="product" productName={props.productName}>
                price-history.data-information
              </T>
            </div>
          )
        }
      >
        {props.withSummary && !props.preRelease && lowestPrice && (
          <div className={styles.summary}>
            <T
              domain="product"
              price={
                <PriceLabel
                  key="price-label"
                  styling="secondary"
                  bold
                  value={lowestPrice.price}
                />
              }
              date={lowestPrice.date}
            >
              price-history.description
            </T>
          </div>
        )}

        {IS_BROWSER && (
          <Graph
            id="pricehistory"
            responsive
            showXGrid={false}
            xScaleData={defaultXScaleData || props.xScaleData}
            yScaleData={defaultYScaleData || props.yScaleData}
            xTickFormat={value => formatDate(value)}
            yTickFormat={value => formatCurrency(value, market.code)}
            renderTooltip={({ xValue, yValue, importantDates }) => (
              <>
                <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
                  {xValue}
                </div>
                <div>
                  <Currency price={yValue} market={market.code} />
                </div>
                {importantDates && importantDates.length > 0 && (
                  <div style={{ marginTop: 5, whiteSpace: 'pre-wrap' }}>
                    {importantDates.map(date => date.name).join('\n')}
                  </div>
                )}
              </>
            )}
            storePrices={Object.keys(props.selectedPrices)
              .filter(
                id => props.priceHistory[id] && props.priceHistory[id].prices
              )
              .map(id => ({
                id,
                prices: props.priceHistory[id].prices,
              }))}
            storePriceColors={props.selectedPrices}
            nodes={
              props.predictedPrices
                ? [
                    props.nodes.map(item => ({
                      date: item.date,
                      value: item.lowestPrice,
                    })),
                    props.predictedPrices.map(item => ({
                      date: item.date,
                      value: item.price,
                    })),
                  ]
                : [
                    props.nodes.map(item => ({
                      date: item.date,
                      value: item.lowestPrice,
                    })),
                  ]
            }
            graphs={defaultGraphs || props.graphs}
            aspectRatio={props.graphAspectRatio}
            legend={props.legend}
          />
        )}
      </Panel>
    </div>
  );
};

const {
  string,
  shape,
  func,
  array,
  object,
  number,
  arrayOf,
  bool,
  node,
} = PropTypes;

PriceHistoryLoader.propTypes = {
  withTitle: bool,
  withSummary: bool,
  nodes: array.isRequired, // eslint-disable-line react/forbid-prop-types
  selectedPrices: object,
  priceHistory: object,
  pageInfo: shape({
    lowestPrice: shape({
      date: string,
      price: number,
    }),
    since: string,
  }).isRequired,
  route: string,
  productName: string,
  predictedPrices: arrayOf(
    shape({
      date: string,
      price: number,
    })
  ),
  priceForecast: arrayOf(
    shape({
      date: string,
      price: number,
    })
  ),
  preRelease: bool,
  graphs: arrayOf(
    shape({
      name: string,
      legend: shape({
        label: string,
        color: string,
        lineType: string,
      }),
      render: func,
      tooltipData: func,
    })
  ),
  graphAspectRatio: number,
  legend: node,
  xScaleData: array,
  yScaleData: array,
  className: string,
};

PriceHistoryLoader.defaultProps = {
  withTitle: true,
  withSummary: true,
  selectedPrices: {},
  priceHistory: {},
  predictedPrices: [],
  priceForecast: [],
  route: null,
  productName: null,
  preRelease: false,
  graphs: undefined,
  graphAspectRatio: undefined,
  legend: undefined,
  xScaleData: [],
  yScaleData: [],
  className: undefined,
};

export default memo(PriceHistoryLoader);
